import { SELF_ALLOCATION_RETIRED } from "@/features/finances/selfAllocationFeature";
import { Navigate, Outlet } from "react-router-dom";
import { RedirectToLogin } from "./RedirectToLogin";
import { useAuthStore, type AuthUser } from "@/store/auth";
import { showsMyB2BInvoices } from "@/utils/contractViews";

interface RoleGuardProps {
  roles: AuthUser["role"][];
}

export function RoleGuard({ roles }: RoleGuardProps) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Access for "Moje faktury B2B" — anyone on a B2B contract now or earlier (regardless
// of role, except external contractors; see utils/contractViews) plus
// director/accountant admins. Must stay aligned with the sidebar visibility check
// in components/layout/Sidebar.tsx.
export function B2BInvoicesGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  const isAdmin = user.role === "director" || user.role === "accountant";
  if (!isAdmin && !showsMyB2BInvoices(user)) return <Navigate to="/" replace />;
  return <Outlet />;
}

// GG (komunikator). Pracownicy GK zawsze; konto podwykonawcy tylko z flagą
// `has_chat_access` nadaną przez CEO. Lustro reguły z backendu
// (apps.chat.permissions.HasChatAccess) i widoczności pozycji w Sidebar.tsx.
export function ChatGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  if (!user.has_chat_permission) return <Navigate to="/" replace />;
  return <Outlet />;
}

// TwoDrive (dysk). Pracownicy GK zawsze; konto podwykonawcy tylko z flagą
// `has_two_drive_access` nadaną przez CEO. Lustro reguły z backendu
// (apps.two_drive.permissions.HasTwoDriveAccess) i widoczności ikony w TopBar.tsx.
export function TwoDriveGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  if (!user.has_two_drive_permission) return <Navigate to="/" replace />;
  return <Outlet />;
}

// DLC module access ("dlc_01" Projekty ZE, "dlc_02" Obieg dokumentów) — granted
// per-user by the CEO in the DLC access panel. CEO sees all; contractors only DLC_01,
// DLC_02 and DLC_12 (the backend filters accessible_dlc_modules accordingly).
// Mirrors the sidebar visibility check in components/layout/Sidebar.tsx.
// A list of modules means "any of them" (e.g. the "Działki" view: full DLC_01 or the
// minimal DLC_01 grant).
type DLCModuleId =
  | "dlc_01" | "dlc_01_min" | "dlc_02" | "dlc_03" | "dlc_04" | "dlc_06"
  | "dlc_07" | "dlc_09" | "dlc_10" | "dlc_12";

export function DLCModuleGuard({
  module,
}: {
  module: DLCModuleId | DLCModuleId[];
}) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  const anyOf = Array.isArray(module) ? module : [module];
  if (!anyOf.some((m) => user.accessible_dlc_modules?.includes(m)))
    return <Navigate to="/" replace />;
  return <Outlet />;
}

// Rejestry WSPÓLNE dla modułów — dziś rejestr urzędów (/paper-send/offices). Urząd jest
// wybierany przy działce (DLC_01), przy korespondencji (DLC_02) i przy e-Doręczeniach
// (DLC_09), a literówkę w jego nazwie czy koncie zauważa ten, kto akurat wypełnia wniosek.
// Zamknięcie rejestru w module korespondencji zostawiało resztę bez możliwości poprawki —
// i to z tego brał się nawyk zakładania duplikatu zamiast korekty. Lustro bramki API
// (apps.accounts.permissions.CanAccessAnyDLCModule).
export function AnyDLCModuleGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  // Minimalny dostęp do DLC_01 (sam widok działek) nie otwiera żadnego modułu.
  if (!user.accessible_dlc_modules?.some((m) => m !== "dlc_01_min"))
    return <Navigate to="/" replace />;
  return <Outlet />;
}

// CEO-only sections. Used by DLC_09 e-Doręczenia for the inbox and mailbox configuration:
// incoming e-Delivery mail carries letters from courts, the tax office and ZUS, so it is the
// CEO's alone. The API enforces the same rule — this guard only avoids showing a door that
// will not open.
export function CeoOnlyGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  if (!user.is_ceo) return <Navigate to="/" replace />;
  return <Outlet />;
}

// "Do zatwierdzenia" — director (role) or CEO, mirroring the API's IsDirectorOrCEO.
export function DirectorOrCeoGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  if (user.role !== "director" && !user.is_ceo) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Company correspondence register ("Dziennik korespondencji", DLC_02) — users granted
// the "Firmowy obieg dokumentów" permission in the CEO panel, plus directors (read-only
// view of all incoming). Everyone else with section access is sent to their own
// correspondence ("Moja korespondencja"). The manage-permission fallback keeps access
// for sessions persisted before the view permission existed.
export function CorrespondenceRegisterGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  const canView =
    user.has_correspondence_register_view_permission ??
    user.has_correspondence_register_permission;
  if (!canView) return <Navigate to="/paper-send/mine" replace />;
  return <Outlet />;
}

// Self-allocation owner view — internal user with the can_self_allocate flag.
export function SelfAllocationOwnerGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  // Rozkład pracy wycofany — własny formularz nie istnieje już dla nikogo;
  // historię ogląda CEO w archiwum (SelfAllocationAdminGuard).
  if (SELF_ALLOCATION_RETIRED) return <Navigate to="/" replace />;
  const isInternal = user.role !== "contractor";
  if (!isInternal || !user.can_self_allocate) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Workload summary view — CEO (with finances) or technical director (without finances).
export function WorkloadSummaryGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  const isTechDirector =
    user.role === "director" && !!user.is_technical_director;
  if (!user.is_ceo && !isTechDirector) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Self-allocation admin view — CEO or technical director with a branch.
export function SelfAllocationAdminGuard() {
  const user = useAuthStore((s) => s.user);
  if (!user) return <RedirectToLogin />;
  // Po wycofaniu funkcji panel jest archiwum wyłącznie dla CEO — dyrektor
  // oddziału nie ma już czego rozpatrywać.
  if (SELF_ALLOCATION_RETIRED) {
    return user.is_ceo ? <Outlet /> : <Navigate to="/" replace />;
  }
  const isBranchDirector =
    user.role === "director" && user.is_technical_director && !!user.branch?.id;
  if (!user.is_ceo && !isBranchDirector) return <Navigate to="/" replace />;
  return <Outlet />;
}
